import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, X, Pencil, Trash2, Loader2, DoorOpen, Check } from "lucide-react";

interface TreatmentRoom { id: number; name: string; capacity: number; equipment?: string; available: boolean; }
function authH() { return { Authorization: `Bearer ${localStorage.getItem("ai2_token")}`, "x-hotel": localStorage.getItem("ai2_hotel") || "" }; }

export default function SpaRooms({ API, isSpaManager }: { API: string; isSpaManager: boolean }) {
  const [rooms, setRooms]   = useState<TreatmentRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]   = useState(false);
  const [edit, setEdit]     = useState<Partial<TreatmentRoom> | null>(null);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/rooms`, { headers: authH() }); setRooms(data); }
    catch { /**/ } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    if (!edit?.name?.trim()) return;
    setSaving(true);
    try {
      if (edit.id) await axios.put(`${API}/rooms/${edit.id}`, edit, { headers: authH() });
      else          await axios.post(`${API}/rooms`, edit, { headers: authH() });
      setModal(false); setEdit(null); fetch();
    } catch { /**/ } finally { setSaving(false); }
  };

  const del = async (id: number) => {
    if (!confirm("Delete this room?")) return;
    await axios.delete(`${API}/rooms/${id}`, { headers: authH() });
    setRooms(r => r.filter(x => x.id !== id));
  };

  const toggle = async (room: TreatmentRoom) => {
    await axios.put(`${API}/rooms/${room.id}`, { ...room, available: !room.available }, { headers: authH() });
    setRooms(r => r.map(x => x.id === room.id ? { ...x, available: !x.available } : x));
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#F2EBD9" }}>Treatment Rooms</div>
          <div style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", marginTop: 2 }}>{rooms.filter(r => r.available).length} available</div>
        </div>
        {isSpaManager && <button className="spa-btn-primary" onClick={() => { setEdit({ name: "", capacity: 1, equipment: "", available: true }); setModal(true); }}><Plus size={14} /> Add Room</button>}
      </div>

      {modal && edit && (
        <div className="spa-modal-overlay" onClick={() => setModal(false)}>
          <div className="spa-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="spa-modal-header">
              <h2 className="spa-modal-title">{edit.id ? "Edit Room" : "New Room"}</h2>
              <button className="spa-modal-close" onClick={() => setModal(false)}><X size={16} /></button>
            </div>
            <div className="spa-modal-body">
              <div className="spa-form-group"><label>Room Name</label><input value={edit.name || ""} onChange={e => setEdit(s => ({ ...s, name: e.target.value }))} placeholder="e.g. Suite 1, Couples Room" /></div>
              <div className="spa-form-row">
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Capacity</label><input type="number" min={1} value={edit.capacity ?? 1} onChange={e => setEdit(s => ({ ...s, capacity: Number(e.target.value) }))} /></div>
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Available</label>
                  <label className="spa-checkbox-row"><input type="checkbox" checked={edit.available ?? true} onChange={e => setEdit(s => ({ ...s, available: e.target.checked }))} /> Active</label>
                </div>
              </div>
              <div className="spa-form-group" style={{ marginTop: 12 }}><label>Equipment / Notes</label><textarea rows={2} value={edit.equipment || ""} onChange={e => setEdit(s => ({ ...s, equipment: e.target.value }))} placeholder="Massage table, hot stones, jacuzzi…" /></div>
            </div>
            <div className="spa-modal-footer">
              <button className="spa-btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="spa-btn-primary" onClick={save} disabled={saving || !edit.name?.trim()}>
                {saving ? <Loader2 size={14} className="spa-spinner" /> : <Check size={14} />}{edit.id ? "Save" : "Create Room"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading…</div>
      ) : rooms.length === 0 ? (
        <div className="spa-fully-booked"><DoorOpen size={28} style={{ marginBottom: 8, opacity: 0.3 }} /><br />No rooms added yet</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
          {rooms.map(r => (
            <div key={r.id} style={{ background: "#0E1E38", border: `1px solid ${r.available ? "rgba(201,168,76,0.18)" : "rgba(255,255,255,0.05)"}`, borderRadius: 14, padding: "16px 18px", opacity: r.available ? 1 : 0.5 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ fontWeight: 700, color: "#F2EBD9", fontSize: 14 }}>{r.name}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {isSpaManager && <button className="spa-action-btn" style={{ padding: "3px 8px", fontSize: 11 }} onClick={() => { setEdit({ ...r }); setModal(true); }}><Pencil size={11} /></button>}
                  {isSpaManager && <button className="spa-action-btn danger" style={{ padding: "3px 8px", fontSize: 11 }} onClick={() => del(r.id)}><Trash2 size={11} /></button>}
                </div>
              </div>
              <div style={{ fontSize: 12, color: "rgba(242,235,217,0.45)", marginBottom: 6 }}>Capacity: {r.capacity} {r.capacity === 1 ? "person" : "people"}</div>
              {r.equipment && <div style={{ fontSize: 11, color: "rgba(242,235,217,0.3)", marginBottom: 10 }}>{r.equipment}</div>}
              <button
                style={{ fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 7, border: `1px solid ${r.available ? "rgba(52,211,153,0.3)" : "rgba(255,255,255,0.1)"}`, background: r.available ? "rgba(52,211,153,0.1)" : "rgba(255,255,255,0.03)", color: r.available ? "#34d399" : "rgba(242,235,217,0.3)", cursor: "pointer", fontFamily: "inherit" }}
                onClick={() => toggle(r)}
              >
                {r.available ? "Available" : "Unavailable"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
